/**
 * ChangeIndicator Component
 * Displays visual indicators for recently modified records
 */

import React from 'react';
import { 
  getChangeIndicatorStyle, 
  getChangeIndicatorText,
  formatLastModified 
} from '../../utils/recordTracker';

/**
 * ChangeIndicator component
 * @param {Object} props
 * @param {Date} props.lastModified - Last modification date
 * @param {boolean} props.showTimestamp - Whether to show timestamp (default: false)
 * @param {number} props.recentHours - Hours to consider recent (default: 24)
 * @param {string} props.position - Position of indicator ('top-right', 'inline', etc.)
 * @param {Object} props.style - Additional styles
 */
const ChangeIndicator = ({ 
  lastModified, 
  showTimestamp = false, 
  recentHours = 24,
  position = 'inline',
  style = {} 
}) => {
  if (!lastModified) return null;

  const indicatorStyle = getChangeIndicatorStyle(lastModified, recentHours);
  const indicatorText = getChangeIndicatorText(lastModified);

  if (!indicatorStyle || !indicatorText) {
    return showTimestamp ? (
      <span 
        style={{
          fontSize: '11px',
          color: '#6b7280',
          fontStyle: 'italic',
          ...style
        }}
        title={`Last modified: ${lastModified.toLocaleString()}`}
      >
        {formatLastModified(lastModified, false)}
      </span>
    ) : null;
  }

  const baseStyle = {
    ...indicatorStyle,
    display: 'inline-block',
    userSelect: 'none',
    cursor: 'help',
    ...style
  };

  if (position === 'top-right') {
    baseStyle.position = 'absolute';
    baseStyle.top = '2px';
    baseStyle.right = '2px';
    baseStyle.zIndex = 10;
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span
        style={baseStyle}
        title={`Last modified: ${lastModified.toLocaleString()}`}
      >
        {indicatorText}
      </span>
      {showTimestamp && (
        <div 
          style={{
            fontSize: '10px',
            color: '#6b7280',
            marginTop: '2px',
            fontStyle: 'italic'
          }}
        >
          {formatLastModified(lastModified, false)}
        </div>
      )}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
    </div>
  );
};

export default ChangeIndicator;
